import React from 'react';

// Uniform "everything else" section for 👁 view panels: shows every filled-in field not listed in `exclude`.
const LABELS = {
    analyteIds: 'Analytes', instrumentId: 'Instrument', isActive: 'Active', isPanel: 'Panel', isQuantitativeTest: 'Quantitative',
    form_dropdown_priority: 'Priority', cptCodes: 'CPT Codes', diagnosis_codes: 'Diagnosis Codes', reflexRuleIds: 'Reflex Rules',
    reflexTestingRules: 'Reflex Testing Rules', canTriggerReflex: 'Can Trigger Reflex', testIds: 'Tests in Panel', turnaroundTime: 'Turnaround Time',
    collection_requirements: 'Collection Requirements', collectionInstructions: 'Collection Instructions', storageInstructions: 'Storage Instructions',
    order_code_description: 'Order Code Description', resultPDFDescriptionNotes: 'Report Notes', reportingTemplate: 'Reporting Template',
    sampleType: 'Sample Type', createdAt: 'Created', updatedAt: 'Last Updated', loinc_code: 'LOINC Code', resultKeyInfoArray: 'Result Keys'
};
const nice = (k) => LABELS[k] || k.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/ Ids?$/i, 's').replace(/^./, (s) => s.toUpperCase());
const isId = (v) => typeof v === 'string' && /^[a-f0-9]{24}$/.test(v);
const isEmpty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length) || (typeof v === 'object' && !Array.isArray(v) && !Object.keys(v).length);
const nameOf = (v) => (v && typeof v === 'object' ? v.name || v.label || v.code || v.title || '' : String(v));
const fmt = (k, v) => {
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (/(At|_date|Date)$/.test(k) && !isNaN(Date.parse(v))) return new Date(v).toLocaleString();
    if (v && typeof v === 'object') return nameOf(v) || Object.entries(v).filter(([kk]) => kk !== '_id').map(([kk, vv]) => `${nice(kk)}: ${typeof vv === 'object' ? nameOf(vv) : vv}`).join(' · ');
    return String(v);
};
const S = {
    section: { marginBottom: 20 },
    title: { fontSize: 11, fontWeight: 700, color: '#888', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #eee' },
    dl: { display: 'grid', gridTemplateColumns: '150px 1fr', rowGap: 8, margin: 0 },
    dt: { fontSize: 13, color: '#555', fontWeight: 600, paddingRight: 8 },
    dd: { fontSize: 13, color: '#222', margin: 0, wordBreak: 'break-word' },
    chip: { display: 'inline-block', padding: '2px 10px', margin: '2px 4px 2px 0', borderRadius: 12, background: '#eef2f7', color: '#334155', fontSize: 12 }
};

export default function RecordDetails({ data, exclude = [], title = 'More Details' }) {
    if (!data) return null;
    const skip = new Set(['_id', '__v', 'id', 'password', ...exclude]);
    const entries = Object.entries(data).filter(([k, v]) => !skip.has(k) && !isEmpty(v));
    const simple = [], lists = [], tables = [];
    entries.forEach(([k, v]) => {
        if (!Array.isArray(v)) simple.push([k, v]);
        else if (v.every((x) => x && typeof x === 'object' && !nameOf(x))) tables.push([k, v]);
        else lists.push([k, v]);
    });
    if (!entries.length) return null;
    return (
        <>
            {(simple.length > 0 || lists.length > 0) && (
                <div style={S.section}>
                    <div style={S.title}>{title}</div>
                    <dl style={S.dl}>
                        {simple.map(([k, v]) => (
                            <React.Fragment key={k}>
                                <dt style={S.dt}>{nice(k)}</dt>
                                <dd style={S.dd}>{isId(v) ? '(linked)' : fmt(k, v)}</dd>
                            </React.Fragment>
                        ))}
                        {lists.map(([k, v]) => {
                            const names = v.map((x) => (x && typeof x === 'object' ? nameOf(x) : String(x))).filter(Boolean);
                            const ids = names.filter(isId).length;
                            return (
                                <React.Fragment key={k}>
                                    <dt style={S.dt}>{nice(k)}</dt>
                                    <dd style={S.dd}>{ids === names.length ? `${names.length} linked` : names.map((n, i) => <span key={i} style={S.chip}>{n}</span>)}</dd>
                                </React.Fragment>
                            );
                        })}
                    </dl>
                </div>
            )}
            {tables.map(([k, v]) => {
                const keys = [...new Set(v.flatMap((r) => Object.keys(r).filter((kk) => !['_id', 'id', '__v'].includes(kk))))];
                return (
                    <div key={k} style={S.section}>
                        <div style={S.title}>{nice(k)}</div>
                        <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                            <thead><tr>{keys.map((kk) => <th key={kk} style={{ whiteSpace: 'nowrap' }}>{nice(kk)}</th>)}</tr></thead>
                            <tbody>{v.map((r, i) => <tr key={i}>{keys.map((kk) => <td key={kk}>{isEmpty(r[kk]) ? '—' : fmt(kk, r[kk])}</td>)}</tr>)}</tbody>
                        </table>
                    </div>
                );
            })}
        </>
    );
}
